import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Share2, Network, ExternalLink } from 'lucide-react';
import { useTracker } from '@/contexts/tracker-context';
import { ItemGrid } from '@/components/tracker/item-grid';
import { PathHintRow } from '@/components/tracker/path-hint-row';
import { BarrenRow } from '@/components/tracker/barren-row';
import { SometimesRow } from '@/components/tracker/sometimes-row';
import { AlwaysRow } from '@/components/tracker/always-row';
import { AlwaysAddButton } from '@/components/tracker/always-add-button';
import { WarpSongDestinations } from '@/components/tracker/warp-song-destinations';
import { KeysanityTracker } from '@/components/tracker/keysanity-tracker';
import { LayoutShareModal } from '@/components/tracker/layout-share-modal';
import { PresetDropdown } from '@/components/tracker/preset-dropdown';
import { EntranceTrackerPage } from '@/components/entrance-tracker/entrance-tracker-page';
import { ALWAYS_HINTS, type AlwaysHintData } from '@/components/tracker/data/always-hints-data';

export function HintsNotesPage() {
  const { state } = useTracker();
  const [shareOpen, setShareOpen] = useState(false);
  const [showEntrancePanel, setShowEntrancePanel] = useState(false);

  const alwaysHints = useMemo<AlwaysHintData[]>(() => {
    const byName = new Map(ALWAYS_HINTS.map((h) => [h.name, h]));
    return state.alwaysHintNames
      .map((name) => byName.get(name) ?? ({ name } as AlwaysHintData))
      .filter(Boolean);
  }, [state.alwaysHintNames]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zelda-gold">Hints & Notes</h1>
        <div className="flex items-center gap-2">
          <PresetDropdown />
          <button
            onClick={() => setShowEntrancePanel((v) => !v)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
              showEntrancePanel
                ? 'bg-zelda-gold/20 text-zelda-gold border border-zelda-gold/30'
                : 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white'
            }`}
            title="Toggle the entrance/mixed-pool tracker side panel"
          >
            <Network size={16} />
            Entrance Panel
          </button>
          <Link
            to="/tracker/entrances"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors text-sm"
            title="Open the full entrance tracker"
          >
            <ExternalLink size={16} />
            Open Entrances
          </Link>
          <button
            onClick={() => setShareOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors text-sm"
            title="Share or import a layout"
          >
            <Share2 size={16} />
            Share Layout
          </button>
          <Link
            to="/tracker/settings"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors text-sm"
          >
            <Settings size={16} />
            Settings
          </Link>
        </div>
      </div>

      {shareOpen && <LayoutShareModal onClose={() => setShareOpen(false)} />}

      <div className={showEntrancePanel ? 'flex gap-3' : 'flex flex-wrap gap-3'}>
        {/* Left column: Item tracker drives the column width; Always inherits it */}
        <div className="flex flex-col gap-2 self-start flex-shrink-0 w-fit">
          {state.useItemTracker && (
            <section
              className="bg-gray-900/50 border border-gray-800 rounded-xl px-2 py-2"
              onContextMenu={(e) => e.preventDefault()}
            >
              <ItemGrid />
            </section>
          )}

          {/* Always Hints — 2-col grid, dual hints on a separate trailing row */}
          <section className="bg-gray-900/50 border border-emerald-900/30 rounded-xl px-3 py-1.5 self-stretch">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                Always ({alwaysHints.length})
              </h2>
              <AlwaysAddButton />
            </div>
            <div className="grid grid-cols-2 gap-1">
              {alwaysHints
                .filter((h) => h.kind !== 'dual')
                .map((hint) => (
                  <AlwaysRow key={hint.name} hint={hint} />
                ))}
            </div>
            {alwaysHints.some((h) => h.kind === 'dual') && (
              <div className="flex flex-wrap gap-1 mt-1">
                {alwaysHints
                  .filter((h) => h.kind === 'dual')
                  .map((hint) => (
                    <AlwaysRow key={hint.name} hint={hint} />
                  ))}
              </div>
            )}
          </section>

          <WarpSongDestinations />
        </div>

        {/* Hints column — uniform 520px width across WoTH / Barren / Sometimes */}
        <div
          className={`space-y-2 ${
            showEntrancePanel ? 'flex-shrink-0 w-fit' : 'flex-1 min-w-[400px]'
          }`}
        >
          {/* Path Hints */}
          {state.nbGoals > 0 && (
            <section className="bg-gray-900/50 border border-gray-800 rounded-xl px-3 py-1.5 w-[520px] max-w-full">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                {state.isWoth ? 'Way of the Hero' : 'Path Hints'} ({state.nbGoals})
              </h2>
              <div className="space-y-0.5">
                {Array.from({ length: state.nbGoals }, (_, i) => (
                  <PathHintRow key={i} id={i} />
                ))}
              </div>
            </section>
          )}

          {/* Barren */}
          {state.nbBarren > 0 && (
            <section className="bg-gray-900/50 border border-red-900/30 rounded-xl px-3 py-1.5 w-[520px] max-w-full">
              <h2 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-1">
                Barren ({state.nbBarren})
              </h2>
              <div className="space-y-0.5">
                {Array.from({ length: state.nbBarren }, (_, i) => (
                  <BarrenRow key={i} id={i} />
                ))}
              </div>
            </section>
          )}

          {/* Sometimes Hints */}
          {state.nbSometimes > 0 && (
            <section className="bg-gray-900/50 border border-gray-800 rounded-xl px-3 py-1.5 w-[520px] max-w-full">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Sometimes Hints ({state.nbSometimes})
                <span className="text-xs text-gray-600 ml-2 font-normal normal-case">click the badge to cycle kind</span>
              </h2>
              <div className="space-y-0.5">
                {Array.from({ length: state.nbSometimes }, (_, i) => (
                  <SometimesRow key={i} id={i} />
                ))}
              </div>
            </section>
          )}

          {/* Keysanity (separate, no width harmonization) */}
          {state.isKeysanity && (
            <section className="bg-gray-900/50 border border-gray-800 rounded-xl px-3 py-1.5 w-fit max-w-full">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Keys {state.isKeyring && <span className="text-yellow-400 text-xs">(Ring)</span>}
              </h2>
              <KeysanityTracker />
            </section>
          )}
        </div>

        {/* Entrance tracker side panel */}
        {showEntrancePanel && (
          <div className="flex-1 min-w-0 bg-gray-900/50 border border-gray-800 rounded-xl px-3 py-2 overflow-hidden">
            <EntranceTrackerPage />
          </div>
        )}
      </div>
    </div>
  );
}
